import { DealDialog } from "@/components/deals/DealDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DealDetails } from "@/core";
import { useGetDealById } from "@/hooks/repository-hooks/deal/use-deal";
import { useGetEventsByDealId } from "@/hooks/repository-hooks/event/use-event";
import { useSourceDeals } from "@/hooks/service-hooks/deals/use-won-deals";
import {
  getAllStages,
  getStageDisplayName,
  getStageValue,
} from "@/utils/dealStage";
import { formatCurrency, formatNumber } from "@/utils/formatting";
import {
  Column,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { format, isValid, parseISO } from "date-fns";
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  ArrowUpDown,
  ExternalLink,
  Eye,
  Filter,
  Loader2,
  Search,
} from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

interface LocationState {
  sourceDealIds?: string[];
  reportType?: string;
  reportName?: string;
}

function SortableHeader({
  column,
  children,
}: {
  column: Column<DealDetails, unknown>;
  children: React.ReactNode;
}) {
  return (
    <Button
      variant="ghost"
      onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      className="h-auto p-0 font-semibold hover:bg-transparent"
    >
      {children}
      {column.getIsSorted() === "asc" ? (
        <ArrowUp className="ml-2 h-4 w-4" />
      ) : column.getIsSorted() === "desc" ? (
        <ArrowDown className="ml-2 h-4 w-4" />
      ) : (
        <ArrowUpDown className="ml-2 h-4 w-4" />
      )}
    </Button>
  );
}

// Helper function to safely format dates
function safeFormatDate(dateValue: string | undefined): string {
  if (!dateValue) return "-";

  try {
    const date = parseISO(dateValue);
    if (!isValid(date)) return "-";
    return format(date, "MMM dd, yyyy");
  } catch {
    return "-";
  }
}

export default function DealsDetailsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;

  const sourceDealIds = state?.sourceDealIds;
  //   const reportType = state?.reportType || "product";
  const reportName = state?.reportName || "Report";

  // Fetch deals using the cloud function
  const { data: deals, isLoading, error } = useSourceDeals(sourceDealIds || []);

  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  
  // Stage filter state
  const [stageFilter, setStageFilter] = useState<string>("all");
  const allStages = getAllStages();

  // Filter deals based on search term and stage filter
  const filteredDeals = useMemo(() => {
    if (!deals) return [];
    
    let filtered = deals;

    // Filter by stage
    if (stageFilter !== "all") {
      const stageValue = getStageValue(stageFilter);
      filtered = filtered.filter((deal) => deal.stage === stageValue);
    }

    // Filter by search term (by name or ID)
    if (searchTerm.trim()) {
      const lowerSearchTerm = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        (deal) =>
          deal.name.toLowerCase().includes(lowerSearchTerm) ||
          deal.id.toLowerCase().includes(lowerSearchTerm),
      );
    }

    return filtered;
  }, [deals, searchTerm, stageFilter]);

  // Sorting state
  const [sorting, setSorting] = useState<SortingState>([
    { id: "name", desc: false },
  ]);

  // Deal dialog state
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  const [dealDialogOpen, setDealDialogOpen] = useState(false);

  // Fetch deal details when a deal is selected
  const { data: selectedDeal } = useGetDealById(selectedDealId || "");

  // Fetch events for the selected deal
  const eventsData = useGetEventsByDealId(
    {
      pagination: {
        limit: 20,
      },
    },
    selectedDealId || "",
  );

  const events = useMemo(() => {
    if (eventsData.isLoading || !eventsData.data) {
      return [];
    }
    return eventsData.data.pages
      .flatMap((page) => page)
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }, [eventsData.data, eventsData.isLoading]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleViewDeal = (dealId: string) => {
    setSelectedDealId(dealId);
    setDealDialogOpen(true);
  };

  // Define columns for the table
  const columns = useMemo<ColumnDef<DealDetails>[]>(
    () => [
      {
        accessorKey: "name",
        header: ({ column }) => (
          <SortableHeader column={column}>Deal Name</SortableHeader>
        ),
        cell: ({ row }) => (
          <span className="font-medium">{row.getValue("name") || "-"}</span>
        ),
      },
      {
        accessorKey: "createdAt",
        header: ({ column }) => (
          <SortableHeader column={column}>Created At</SortableHeader>
        ),
        cell: ({ row }) => (
          <span className="font-medium">
            {safeFormatDate(row.getValue("createdAt"))}
          </span>
        ),
        sortingFn: (rowA, rowB) => {
          const dateA = parseISO(rowA.original.createdAt);
          const dateB = parseISO(rowB.original.createdAt);
          if (!isValid(dateA) || !isValid(dateB)) return 0;
          return dateA.getTime() - dateB.getTime();
        },
      },
      {
        accessorKey: "quantity",
        header: ({ column }) => (
          <SortableHeader column={column}>Quantity (Miles)</SortableHeader>
        ),
        cell: ({ row }) => (
          <span className="font-semibold">
            {formatNumber(row.getValue("quantity"))}
          </span>
        ),
      },
      {
        accessorKey: "amount",
        header: ({ column }) => (
          <SortableHeader column={column}>Amount</SortableHeader>
        ),
        cell: ({ row }) => (
          <span className="font-semibold text-green-600">
            {formatCurrency(row.getValue("amount"))}
          </span>
        ),
      },
      {
        accessorKey: "hubspotLink",
        header: () => (
          <div className="flex items-center justify-start">HubSpot Link</div>
        ),
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="sm"
            className="gap-2 h-8"
            onClick={() => window.open(row.original.hubspotLink, "_blank")}
          >
            <ExternalLink className="h-4 w-4" />
            Open in HubSpot
          </Button>
        ),
        enableSorting: false,
      },
      {
        accessorKey: "id",
        header: () => (
          <div className="flex items-center justify-start">Actions</div>
        ),
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="sm"
            className="gap-2 h-8"
            onClick={() => handleViewDeal(row.original.id)}
          >
            <Eye className="h-4 w-4" />
            View Details
          </Button>
        ),
        enableSorting: false,
      },
    ],
    [],
  );

  // Create table instance
  const table = useReactTable({
    data: filteredDeals,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  // Virtualization setup
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { rows } = table.getRowModel();

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 40, // Row height in pixels
    overscan: 10, // Number of rows to render outside visible area
  });

  if (!sourceDealIds || sourceDealIds.length === 0) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64">
            <h3 className="text-lg font-semibold mb-2">
              No Source Deal IDs Provided
            </h3>
            <p className="text-muted-foreground text-center mb-4">
              Please select a report with source deals to view details.
            </p>
            <Button onClick={handleBack} variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Reports
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64">
            <h3 className="text-lg font-semibold mb-2">Error Loading Deals</h3>
            <p className="text-muted-foreground text-center mb-4">
              There was an error loading the deal details. Please try again.
            </p>
            <Button onClick={handleBack} variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Reports
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleBack}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div>
            <h2 className="text-2xl font-bold">{reportName} - Source Deals Details</h2>
            <h3 className="text-lg text-muted-foreground">
              {reportName} ({formatNumber(filteredDeals.length)} of {formatNumber(deals?.length || 0)} deals)
            </h3>
          </div>
        </div>
      </div>

      {/* Search Input and Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or ID..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              if (e.target.value.trim()) {
                setStageFilter("all");
              }
            }}
            className="pl-10"
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="py-4">
              <Filter className="h-4 w-4 mr-2" />
              Stage:{" "}
              {stageFilter === "all"
                ? "All"
                : getStageDisplayName(stageFilter)}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Filter by Stage</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                setStageFilter("all");
                setSearchTerm("");
              }}
            >
              All
            </DropdownMenuItem>
            {allStages.map((stage) => (
              <DropdownMenuItem
                key={stage.key}
                onClick={() => {
                  setStageFilter(stage.key);
                  setSearchTerm("");
                }}
              >
                {stage.displayName}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        {(searchTerm || stageFilter !== "all") && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearchTerm("");
              setStageFilter("all");
            }}
            className="h-9"
          >
            Clear
          </Button>
        )}
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Loading deal details...</p>
          </CardContent>
        </Card>
      ) : deals && deals.length > 0 ? (
        <div className="rounded-md border overflow-hidden overflow-x-auto relative">
          <div
            ref={tableContainerRef}
            className="h-[900px] overflow-auto"
            style={{
              contain: "strict",
            }}
          >
            <Table>
              <TableHeader className="sticky top-0 z-40 bg-background">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id} className="border-b">
                    {headerGroup.headers.map((header, colIdx) => (
                      <TableHead
                        key={header.id}
                        className={`relative group whitespace-nowrap ${
                          colIdx === 0 || colIdx === 1
                            ? "text-left xl:sticky xl:left-0 xl:z-30 xl:bg-background xl:after:absolute xl:after:content-[''] xl:after:inset-y-0 xl:after:right-0 xl:after:w-px xl:after:bg-border"
                            : header.column.id === "quantity" ||
                                header.column.id === "amount"
                              ? "text-right"
                              : "text-left"
                        } border-r last:border-r-0`}
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            className={`flex items-center ${
                              header.column.id === "quantity" ||
                              header.column.id === "amount"
                                ? "justify-end"
                                : "justify-start"
                            }`}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                          </div>
                        )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      No results.
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {/* Spacer row for virtual scrolling */}
                    {rowVirtualizer.getVirtualItems().length > 0 && (
                      <TableRow
                        aria-hidden="true"
                        className="pointer-events-none"
                      >
                        <TableCell
                          colSpan={columns.length}
                          style={{
                            height: `${rowVirtualizer.getVirtualItems()[0]?.start ?? 0}px`,
                            padding: 0,
                            border: 0,
                          }}
                        />
                      </TableRow>
                    )}
                    {/* Virtualized visible rows */}
                    {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                      const row = rows[virtualRow.index];
                      return (
                        <TableRow
                          key={row.id}
                          data-index={virtualRow.index}
                          ref={rowVirtualizer.measureElement}
                          className="group odd:bg-muted/30 hover:bg-muted/50 border-b transition-colors duration-200"
                        >
                          {row.getVisibleCells().map((cell, colIdx) => (
                            <TableCell
                              key={cell.id}
                              className={`whitespace-nowrap ${
                                colIdx === 0 || colIdx === 1
                                  ? "text-left xl:sticky xl:left-0 xl:z-20 xl:bg-background transition-colors duration-200 xl:before:absolute xl:before:content-[''] xl:before:inset-0 xl:before:pointer-events-none xl:before:transition-colors xl:before:duration-200 group-odd:xl:before:bg-muted/30 group-hover:xl:before:bg-muted/50 xl:before:z-0 xl:after:absolute xl:after:content-[''] xl:after:inset-y-0 xl:after:right-0 xl:after:w-px xl:after:bg-border xl:after:z-10"
                                  : cell.column.id === "quantity" ||
                                      cell.column.id === "amount"
                                    ? "text-right"
                                    : "text-left"
                              } border-r last:border-r-0 h-10`}
                            >
                              {colIdx === 0 || colIdx === 1 ? (
                                <div className="relative xl:z-10 flex items-center h-full">
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext(),
                                  )}
                                </div>
                              ) : (
                                <div
                                  className={`flex items-center h-full ${
                                    cell.column.id === "quantity" ||
                                    cell.column.id === "amount"
                                      ? "justify-end"
                                      : "justify-start"
                                  }`}
                                >
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext(),
                                  )}
                                </div>
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                    {/* Spacer row after virtual items */}
                    {rowVirtualizer.getVirtualItems().length > 0 && (
                      <TableRow
                        aria-hidden="true"
                        className="pointer-events-none"
                      >
                        <TableCell
                          colSpan={columns.length}
                          style={{
                            height: `${
                              rowVirtualizer.getTotalSize() -
                              (rowVirtualizer.getVirtualItems()[
                                rowVirtualizer.getVirtualItems().length - 1
                              ]?.end ?? 0)
                            }px`,
                            padding: 0,
                            border: 0,
                          }}
                        />
                      </TableRow>
                    )}
                  </>
                )}
              </TableBody>
            </Table>
          </div>
          {/* Footer outside scrollable area
          <div className="border-t">
            <Table>
              <TableFooter>
                <TableRow className="group bg-muted/30 hover:bg-muted/50 border-t-2 border-b transition-colors duration-200">
                  <TableCell className="whitespace-nowrap text-left border-r last:border-r-0 xl:sticky xl:left-0 xl:z-20 xl:bg-background transition-colors duration-200 xl:before:absolute xl:before:content-[''] xl:before:inset-0 xl:before:pointer-events-none xl:before:transition-colors xl:before:duration-200 xl:before:bg-muted/30 group-hover:xl:before:bg-muted/50 xl:before:z-0 xl:after:absolute xl:after:content-[''] xl:after:inset-y-0 xl:after:right-0 xl:after:w-px xl:after:bg-border xl:after:z-10">
                    <div className="relative xl:z-10 flex items-center">
                      <span className="font-semibold">Total</span>
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-left border-r last:border-r-0 xl:sticky xl:left-0 xl:z-20 xl:bg-background transition-colors duration-200 xl:before:absolute xl:before:content-[''] xl:before:inset-0 xl:before:pointer-events-none xl:before:transition-colors xl:before:duration-200 xl:before:bg-muted/30 group-hover:xl:before:bg-muted/50 xl:before:z-0 xl:after:absolute xl:after:content-[''] xl:after:inset-y-0 xl:after:right-0 xl:after:w-px xl:after:bg-border xl:after:z-10">
                    <div className="relative xl:z-10 flex items-center" />
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right border-r last:border-r-0">
                    <div className="flex items-center justify-end">
                      <span className="font-semibold">
                        {formatNumber(
                          deals.reduce(
                            (sum: number, deal: DealDetails) =>
                              sum + deal.quantity,
                            0,
                          ),
                        )}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right border-r last:border-r-0">
                    <div className="flex items-center justify-end">
                      <span className="font-semibold text-green-600">
                        {formatCurrency(
                          deals.reduce(
                            (sum: number, deal: DealDetails) =>
                              sum + deal.amount,
                            0,
                          ),
                        )}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-left border-r last:border-r-0">
                    <div className="flex items-center" />
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-left border-r last:border-r-0">
                    <div className="flex items-center" />
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </div> */}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64">
            <h3 className="text-lg font-semibold mb-2">
              No Source Deals Found
            </h3>
            <p className="text-muted-foreground text-center mb-4">
              No source deal details were found for the provided IDs.
            </p>
            <Button onClick={handleBack} variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Reports
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Deal Dialog */}
      <DealDialog
        open={dealDialogOpen}
        onOpenChange={setDealDialogOpen}
        deal={selectedDeal || null}
        events={events}
        isLoadingEvent={eventsData.isLoading}
      />
    </div>
  );
}
